import { useEffect, useState } from "react";
import { Fire, Plus, CheckSquare } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import BadgeWall from "../../components/BadgeWall";

const DAY_LABELS = ["M", "T", "W", "T", "F", "S", "S"];

export function HabitRow({ habit, onToggle, readOnly = false }) {
  const today = new Date().toISOString().slice(0, 10);
  return (
    <div className="card-flat p-5" data-testid={`habit-row-${habit.id}`}>
      <div className="flex items-center justify-between gap-3 mb-4">
        <div className="min-w-0">
          <div className="font-semibold truncate" data-testid="habit-name">{habit.name}</div>
          <div className="tracker text-[10px] text-zinc-500 mt-1">
            Target {habit.target_days_per_week}×/week · {habit.week_done}/{habit.target_days_per_week} done this week
          </div>
        </div>
        <div className={`flex items-center gap-1.5 shrink-0 ${habit.streak > 0 ? "text-amber-400" : "text-zinc-600"}`} data-testid="habit-streak">
          <Fire size={18} weight={habit.streak > 0 ? "fill" : "duotone"} />
          <span className="font-display text-2xl">{habit.streak}</span>
          <span className="tracker text-[9px] mt-1">day{habit.streak === 1 ? "" : "s"}</span>
        </div>
      </div>
      <div className="flex gap-2">
        {habit.recent_days.map((d) => {
          const dt = new Date(`${d.date}T12:00:00Z`);
          const isToday = d.date === today;
          return (
            <button
              key={d.date}
              onClick={() => !readOnly && onToggle(habit, d.date)}
              disabled={readOnly}
              title={d.date}
              className={`flex-1 py-2 flex flex-col items-center gap-1 border transition-colors ${
                d.checked
                  ? "bg-emerald-500/20 border-emerald-500/50 text-emerald-300"
                  : "bg-[#0A0A0A] border-white/10 text-zinc-500 hover:border-white/30"
              } ${isToday ? "ring-1 ring-brand" : ""} ${readOnly ? "cursor-default" : ""}`}
              data-testid={`habit-day-${habit.id}-${d.date}`}
            >
              <span className="tracker text-[9px]">{DAY_LABELS[(dt.getUTCDay() + 6) % 7]}</span>
              <span className="text-xs font-mono">{dt.getUTCDate()}</span>
              <CheckSquare size={14} weight={d.checked ? "fill" : "regular"} />
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function ClientHabits() {
  const { user } = useAuth();
  const [habits, setHabits] = useState(null);
  const [badgeKey, setBadgeKey] = useState(0);

  const load = async () => {
    try {
      const { data } = await api.get(`/clients/${user.id}/habits`, { params: { days: 7 } });
      setHabits(data);
    } catch (_e) {
      setHabits([]);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const onToggle = async (habit, date) => {
    try {
      const { data } = await api.post(`/habits/${habit.id}/toggle`, { date });
      setHabits((hs) => hs.map((h) => (h.id === habit.id ? data.habit : h)));
      (data.new_badges || []).forEach((b) => toast.success(`Badge unlocked — ${b.name}!`, { description: b.description }));
      if (data.new_badges?.length) setBadgeKey((k) => k + 1);
      else if (data.checked && data.habit.streak >= 3) toast.success(`${data.habit.streak}-day streak on "${habit.name}" 🔥`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't update");
    }
  };

  return (
    <div className="space-y-8" data-testid="client-habits-page">
      <div>
        <div className="tracker text-brand mb-3">[ Daily habits ]</div>
        <h1 className="font-display text-4xl sm:text-5xl">HABITS.</h1>
        <p className="text-zinc-400 mt-3">Tick each day you complete a habit. Streaks are earned, not given.</p>
      </div>

      {habits === null ? (
        <div className="tracker text-zinc-500">Loading…</div>
      ) : habits.length === 0 ? (
        <div className="card-flat p-10 text-center" data-testid="habits-empty">
          <Plus size={32} weight="duotone" className="mx-auto text-zinc-600 mb-3" />
          <div className="font-display text-2xl mb-2">No habits yet</div>
          <div className="text-zinc-500 text-sm">Your coach will assign daily habits for you to track here.</div>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {habits.map((h) => (
            <HabitRow key={h.id} habit={h} onToggle={onToggle} />
          ))}
        </div>
      )}

      <BadgeWall clientId={user.id} refreshKey={badgeKey} />
    </div>
  );
}
